import { format } from "date-fns";
import React from "react";

import {
	Chip,
	type ICellRendererParams,
	Link,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
} from "@performyard/py-components";
import type { MeetingModelJson } from "@performyard/service-contracts";

import MeetingCalendarIcon from "./MeetingCalendarIcon";
import MeetingRecurringIcon, { type RecurringSeriesTooltipInfo } from "./MeetingRecurringIcon";
import MeetingRowOptionsDropdown from "./MeetingRowOptionsDropdown";
import MeetingVideoConferencingIcon from "./MeetingVideoConferencingIcon";

import { useCalendarFeatureEnabled, useVideoConferencingFeatureEnabled } from "../hooks";

import { appendMeetingBreadcrumbSource, useMeetingBreadcrumbSource } from "../../utils/breadcrumbSource";

import { MEETING_STATUS_CONFIG } from "../config";

type MeetingsTableProps = {
	meetings: MeetingModelJson[];
	isFetchingMeetings: boolean | undefined;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
	onEditMeeting: (meeting: MeetingModelJson) => void;
	workspaceId: string;
	workspaceCreatorId: string;
	/** Visible active or finished series keyed by ID; deleted series are intentionally absent. */
	recurringSeriesById: Record<string, RecurringSeriesTooltipInfo>;
};

const DataTable = (props: MeetingsTableProps): React.ReactElement => {
	const {
		meetings,
		isFetchingMeetings,
		activeSort,
		activeSortDirection,
		handleColumnHeaderClick,
		userId,
		onEditMeeting,
		workspaceId,
		workspaceCreatorId,
		recurringSeriesById,
	} = props;

	const breadcrumbSource = useMeetingBreadcrumbSource();
	const showCalendarIcon = useCalendarFeatureEnabled();
	const showVideoConferencingIcon = useVideoConferencingFeatureEnabled();
	const showIntegrationsColumn = showCalendarIcon || showVideoConferencingIcon;

	const getStatusChip = (status: MeetingModelJson["status"]): React.ReactElement => {
		const { label, color } = MEETING_STATUS_CONFIG[status] || MEETING_STATUS_CONFIG.active;
		return <Chip label={label} color={color} size={24} />;
	};

	const formatMeetingDateTime = (startDateTime: Date, endDateTime: Date): React.ReactElement => {
		const isSameDay = startDateTime.toDateString() === endDateTime.toDateString();
		const formatTime = (date: Date): string => format(date, "h:mmaaa");
		const formatDateWithTime = (date: Date): string => format(date, "MMM d, yyyy h:mmaaa");

		if (isSameDay) {
			// Format: "Jan 9, 2026 1:00pm–3:00pm"
			const dateStr = format(startDateTime, "MMM d, yyyy");
			const timeRange = `${formatTime(startDateTime)}–${formatTime(endDateTime)}`;

			return (
				<Text size="paragraph-3" weight="regular">
					{dateStr} {timeRange}
				</Text>
			);
		}

		// Format: "Jan 9, 2026 12:00am–Jan 10, 2026 12:00am"
		return (
			<Text size="paragraph-3" weight="regular">
				{formatDateWithTime(startDateTime)}–{formatDateWithTime(endDateTime)}
			</Text>
		);
	};

	const columnDefs: PYColumnDef<MeetingModelJson>[] = [
		...(showIntegrationsColumn
			? [
					{
						field: "integrations",
						headerName: "",
						sortable: false,
						resizable: false,
						width: 64,
						minWidth: 64,
						maxWidth: 64,
						cellClass: "flex items-center",
						cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
							const meeting = params.data;
							if (!meeting) return null;
							return (
								<div className="flex h-full w-full items-center justify-center gap-1">
									{showCalendarIcon && <MeetingCalendarIcon meeting={meeting} />}
									{showVideoConferencingIcon && <MeetingVideoConferencingIcon meeting={meeting} />}
								</div>
							);
						},
					},
				]
			: []),
		{
			field: "name",
			headerName: "Meeting Name",
			sortable: true,
			resizable: false,
			width: 488,
			minWidth: 300,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				const recurringSeries = meeting.recurringSeriesId
					? recurringSeriesById[meeting.recurringSeriesId]
					: undefined;
				const href = appendMeetingBreadcrumbSource(
					`/meeting-workspaces/${workspaceId}/meetings/${meeting._id}`,
					breadcrumbSource
				);
				return (
					<div className="flex h-full w-full min-w-0 items-center gap-1">
						<Link href={href} className="min-w-0 no-underline hover:underline">
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
							>
								{meeting.name}
							</Text>
						</Link>
						{recurringSeries && <MeetingRecurringIcon series={recurringSeries} />}
					</div>
				);
			},
		},
		{
			field: "startDateTime",
			headerName: "Meeting Date",
			sortable: true,
			resizable: false,
			width: 320,
			minWidth: 240,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						{formatMeetingDateTime(meeting.startDateTime, meeting.endDateTime)}
					</div>
				);
			},
		},
		{
			field: "agendaItems",
			headerName: "Agenda Items",
			sortable: true,
			resizable: false,
			width: 120,
			minWidth: 120,
			maxWidth: 120,
			cellClass: "flex items-center justify-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center justify-center">
						<Text size="paragraph-3" weight="regular">
							{meeting.agendaItemCount ?? 0}
						</Text>
					</div>
				);
			},
		},
		{
			field: "actionItems",
			headerName: "Action Items",
			sortable: true,
			resizable: false,
			width: 120,
			minWidth: 120,
			maxWidth: 120,
			cellClass: "flex items-center justify-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center justify-center">
						<Text size="paragraph-3" weight="regular">
							{meeting.actionItemCount ?? 0}
						</Text>
					</div>
				);
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: true,
			resizable: false,
			width: 120,
			minWidth: 120,
			maxWidth: 120,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				return <div className="flex h-full w-full min-w-0 items-center">{getStatusChip(meeting.status)}</div>;
			},
		},
		{
			field: "control",
			headerName: "",
			sortable: false,
			resizable: false,
			width: 56,
			minWidth: 56,
			maxWidth: 56,
			cellClass: "flex items-center justify-center",
			cellRenderer: (params: ICellRendererParams<MeetingModelJson>) => {
				const meeting = params.data;
				if (!meeting) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center justify-center">
						<MeetingRowOptionsDropdown
							meeting={meeting}
							userId={userId}
							onEditMeeting={onEditMeeting}
							workspaceCreatorId={workspaceCreatorId}
						/>
					</div>
				);
			},
		},
	];

	const onSortChanged = (event: SortChangedEvent<MeetingModelJson>) => {
		const sortedColumn = event.api.getColumnState().find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="meetingsTableContainer">
			<Table<MeetingModelJson>
				autoHeight
				columnSizing="fit-grid"
				rowData={meetings}
				columnDefs={columnDefs}
				loading={isFetchingMeetings}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Meetings Found"
				initialState={{
					sort: {
						sortModel: activeSort
							? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default DataTable;
